(ns frontend.components.inputs.financials
  (:require
   [cljs.reader :as edn]
   [common.currencies :as currencies]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.components.inputs.footprint :refer [display-string editor viewer]]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [reagent.core :as r]))

(def row-keys [:revenue :ebitda])

(defn default-row
  [years]
  (reduce (fn [acc year]
            (assoc acc year nil))
          {}
          years))

(defn default-value
  []
  (let [d (js/Date.)
        current-year (.getFullYear d)
        years (range (- current-year 3) (+ current-year 4))]
    (reduce (fn [acc row-key]
              (assoc acc row-key (default-row years)))
            {:currency (->> @session/tenant
                            :tenant/default-currency)}
            row-keys)))

(defn render
  [dom-atom args]
  (let [{:keys [:value :placeholder :class-name
                :has-focus?
                :on-focus! :on-blur! :on-change!
                :on-key-down! :disabled?]}
        args
        edit-value (or (:temp-value @dom-atom)
                       (when (map? value) value))
        render-value (or edit-value
                         (when (map? value) value)
                         (default-value))
        clear-state! (fn []
                       (swap! dom-atom dissoc :temp-value :decimals))
        years (->> (dissoc render-value :currency)
                   vals
                   (map keys)
                   (apply concat)
                   distinct
                   sort)
        currency (get render-value :currency)]
    [:table.oppo-financials
     [:thead
      [:tr.oppo-financials-header-row
       [:th.oppo-financials-cell.oppo-financials-cell--header
        [:select.oppo-financials-currency-selector
         {:ref       (fn [e]
                       (when (and e
                                  (not= e (:input-dom-node @dom-atom)))
                         (swap! dom-atom assoc :input-dom-node e)))
          :value     (if currency currency "")
          :on-change (fn [e]
                       (let [new-value (assoc render-value
                                              :currency
                                              (-> e
                                                  .-target
                                                  .-value
                                                  keyword))]
                         (swap! dom-atom assoc :temp-value new-value)
                         (when on-change!
                           (on-change! e new-value))
                         (when on-blur!
                           (on-blur! e new-value clear-state!))))}
         (fu/add-separator
          (doall (for [[k v] currencies/currencies]
                   ^{:key (str "financials-table-thead-currency-selector-" k)}
                   [:option
                    {:key   k
                     :value k}
                    v])))]]
       (doall
        (for [year years]
          ^{:key (str "financials-header-cell-" year)}
          [:th.oppo-financials-cell.oppo-financials-cell--header
           year
          ]))]]
     [:tbody
      (doall
       (for [row-key row-keys
             :let [row-data (get render-value row-key)]]
         ^{:key (str "financials-row-" row-key)}
         [:tr.oppo-financials-row
          [:td (loc/tr row-key)]
          (doall
           (for [year years
                 :let [cell-value (get row-data year)]]
             ^{:key (str "financials-cell-" row-key year)}
             [:td.oppo-financials-cell
              [:input
               {:placeholder (or placeholder "")
                :type        "text"
                :value       (let [draw-value cell-value
                                   min-decimal-count (if (nil? draw-value)
                                                       -1
                                                       (or (get-in @dom-atom [:decimals row-key year :decimal-count])
                                                           -1))
                                   edit-negative? (get-in @dom-atom [:decimals row-key year :negative?])]
                               (or (when draw-value
                                     (if (= draw-value "")
                                       (when edit-negative? "-")
                                       (fu/format-with-separators draw-value min-decimal-count edit-negative?)))
                                   ""))
                :on-change   (fn [e]
                               (let [input (-> e
                                               .-target
                                               .-value)
                                     float-value (fu/parse-float-info input "")
                                     new-cell-value (:float-value float-value)
                                     new-value (assoc-in render-value [row-key year] new-cell-value)]
                                 (swap! dom-atom assoc
                                   :temp-value
                                   new-value)
                                 (swap! dom-atom assoc-in
                                   [:decimals row-key year]
                                   {:decimal-count (or (:decimal-count float-value) -1)
                                    :negative?     (or (:negative? float-value) false)})
                                 (when on-change!
                                   (on-change! e new-value))))
                :on-blur     (fn [e]
                               (let [new-value (if (= cell-value "")
                                                 (assoc-in render-value [row-key year] nil)
                                                 render-value)]
                                 (swap! dom-atom assoc :temp-value new-value)
                                 (when on-blur!
                                   (on-blur! e new-value clear-state!))))
                :on-key-down on-key-down!}]
             ]))]))]]))

(defmethod editor :editor.type/financials
  [args]
  (let [dom-atom (or (:state-atom args) (r/atom nil))
        set-focusable! (:set-focusable-element! args)
        set-cancel-edit! (:set-cancel-edit! args)
        set-save! (:set-save! args)]
    (r/create-class
     {:display-name           "Financials"
      :component-did-mount    (fn [args]
                                (let []
                                  (when set-focusable!
                                    (set-focusable! (fn [_]
                                                      (when-let [dom-node (:input-dom-node @dom-atom)]
                                                        (.focus dom-node)))))
                                  (when set-cancel-edit!
                                    (set-cancel-edit! (fn [_]
                                                        (swap! dom-atom dissoc :temp-value)))) ;; save complete -> reset temp state
                                  (when set-save!
                                    (set-save! (fn [_]
                                                 (when-let [dom-node (:input-dom-node @dom-atom)]
                                                   (.blur dom-node)))))))
      :component-will-unmount (fn []
                                (swap! dom-atom dissoc :input-dom-node))
      :reagent-render         (fn [args]
                                (render dom-atom args))})))

(defn financials-array
  [args]
  (let [financials (:value args)
        financials (or financials (default-value))
        currency (->> (get financials :currency)
                      currencies/get-label)
        years (->> (dissoc financials :currency)
                   vals
                   (map keys)
                   (apply concat)
                   distinct
                   sort)
        header (concat [currency] years)
        rows (for [row-key row-keys
                   :let [row-data (get financials row-key)]]
               (concat [row-key]
                       (for [year years
                             :let [cell-value (get row-data year)]]
                         cell-value)))]
    (concat [header]
            rows)))

(defmethod display-string :editor.type/financials
  [args]
  (let [value (:value args)
        decoded (cond (string? value) (edn/read-string value)
                      (sequential? value) value
                      :else value)
        arr (financials-array (merge args
                                     {:value decoded}))]
    (cu/tab-separated-string arr)))


(defn financials-table
  [args]
  (let [value (financials-array args)
        header (first value)
        data (rest value)]
    [:div.preview.preview-financials
     [:table.oppo-financials
      [:thead
       [:tr.oppo-financials-header-row
        (doall
         (for [h header]
           ^{:key (str h)}
           [:th.oppo-financials-cell.oppo-financials-cell--header
            h]))]]
      [:tbody
       (doall
        (for [[metric & _ :as row] data]
          ^{:key (str "row-" metric)}
          [:tr.oppo-financials-row
           (doall
            (for [[cell title] (map vector row header)]
              ^{:key (str "cell-" metric "-" title)}
              [:td.oppo-financials-cell
               cell]))]))]]]))

(defmethod viewer :editor.type/financials
  [args]
  (r/create-class
   {:display-name   "financials/viewer"
    :reagent-render (fn [args]
                      [financials-table args])}))
